/* eslint-disable react-refresh/only-export-components -- this file exports pre-constructed JSX element instances (not component functions) for the route table; Fast Refresh does not apply to static element constants. */
import { lazy, Suspense, type ReactElement } from 'react';
import { SelectSite } from '@components/Placeholder';
import { ProtectedRoute } from '@components/ProtectedRoute/ProtectedRoute';
import { PortfolioPage } from '@components/Pages/PortfolioPage/PortfolioPage';
import { MonitorPage } from '@components/Pages/MonitorPage/MonitorPage';
import { DashboardPage } from '@components/Pages/DashboardPage/DashboardPage';
import { EquipmentPage } from '@components/Pages/EquipmentPage/EquipmentPage';
import { ActivitiesPage } from '@components/Pages/ActivitiesPage/ActivitiesPage';
import { AnalyticsPage } from '@components/Pages/AnalyticsPage/AnalyticsPage';
import { LegacyAnalyticsRedirect } from '@components/Pages/AnalyticsPage/LegacyAnalyticsRedirect';
import { LogisticsPage } from '@components/Pages/LogisticsPage/LogisticsPage';
import { DailyRouteReportPage } from '@components/Pages/AnalyticsPage/Reports/DailyRoute/DailyRouteReportPage';
import { IncidentReportReportPage } from '@components/Pages/AnalyticsPage/Reports/IncidentReport/IncidentReportReportPage';
import { ObserveLocationReportPage } from '@components/Pages/AnalyticsPage/Reports/ObserveLocation/ObserveLocationReportPage';
import { ContractorAnalysisReportPage } from '@components/Pages/AnalyticsPage/Reports/ContractorAnalysis/ContractorAnalysisReportPage';
import { DocumentsPage } from '@components/Pages/DocumentsPage/DocumentsPage';
import { AnnotationsPage } from '@components/Pages/AnnotationsPage/AnnotationsPage';
import { FlowViewPage } from '@components/Pages/FlowViewPage/FlowViewPage';
import { SubcontractorsPage } from '@components/Pages/SubcontractorsPage/SubcontractorsPage';
import { ProductivityPage } from '@components/Pages/ProductivityPage/ProductivityPage';
import { FinancePage } from '@components/Pages/FinancePage/FinancePage';
import { SchedulerPageSkeleton } from '@components/Pages/SchedulerPage/SchedulerPageSkeleton';
import { CommsPage } from '@components/Pages/CommsPage/CommsPage';
import { CamerasPage } from '@components/Pages/CamerasPage/CamerasPage';
import { ProjectMergePage } from '@components/Pages/ProjectMergePage/ProjectMergePage';
import { AdminPage } from '@components/Pages/AdminPage/AdminPage';
import { SecurityPage } from '@components/Pages/SecurityPage/SecurityPage';
import { AlertSettingsPage } from '@components/Pages/AlertSettingsPage/AlertSettingsPage';
import { HelpCenterPage } from '@components/Pages/HelpCenterPage/HelpCenterPage';
import { GuideReaderPage } from '@components/Pages/HelpCenterPage/GuideReaderPage';
const SchedulerPage = lazy(() =>
  import('@components/Pages/SchedulerPage').then((m) => ({ default: m.SchedulerPage })),
);

const CadPage = lazy(() => import('@components/Pages/CadPage/CadPage'));
// AI Agent loads ~900KB of chart/PDF deps (apexcharts, html2canvas-pro,
// jspdf). Lazy so users who never visit /ai-agent don't pay the
// bundle cost up front. Mirrors the CadPage pattern.
const AiAgentPage = lazy(() =>
  import('@components/Pages/AiAgentPage/AiAgentPage').then((m) => ({ default: m.AiAgentPage })),
);

// Worker Timesheet drags in luxon + (eventually) the Excel-export
// module — keep it out of the initial bundle until the user opens
// an archived timesheet report. Same lazy pattern as AiAgentPage.
const WorkerTimesheetReportPage = lazy(() =>
  import(
    '@components/Pages/AnalyticsPage/Reports/WorkerTimesheet/WorkerTimesheetReportPage'
  ).then((m) => ({ default: m.WorkerTimesheetReportPage })),
);

// Project Overview drags in the div-drawn charts + the exceljs export
// module — keep it out of the initial bundle until the user opens an
// archived project-overview report. Same lazy pattern as WorkerTimesheet.
const ProjectOverviewReportPage = lazy(() =>
  import(
    '@components/Pages/AnalyticsPage/Reports/ProjectOverview/ProjectOverviewReportPage'
  ).then((m) => ({ default: m.ProjectOverviewReportPage })),
);

/* Home. The cross-project portfolio is for Trusstor admins and executives
   (`portfolio.view`); everyone else lands on their working site's dashboard,
   so every "go home" link (logo, top bar, login, a refused page) still works. */
export const PortfolioElement = (
  <ProtectedRoute feature="portfolio.view" fallbackTo="/dashboard">
    <PortfolioPage />
  </ProtectedRoute>
);
export const MonitorElement = <MonitorPage />;
/* The live monitor lives at /monitor/. /dashboard/:siteId is the Command
   Center page — Lovable-styled layout that fuses real Trusstor data
   (workforce, building view, safety) with mocked surfaces for upstreams
   not yet integrated (schedule, QA, weather, AI agent). */
export const DashboardElement         = <DashboardPage />;
export const DashboardRedirectElement = <SelectSite label="Dashboard" />;
export const ActivitiesElement = <ActivitiesPage />;
export const ActivitiesRedirectElement = <SelectSite label="Activities & Issues" />;
/* Analytics and its reports: hidden from roles without `analytics.view`
   (Limited). The server refuses their reads too — this only spares the
   empty page (role-permissions spec, ruling 8). */
const analyticsOnly = (page: ReactElement) => <ProtectedRoute feature="analytics.view">{page}</ProtectedRoute>;
export const AnalyticsElement = analyticsOnly(<AnalyticsPage />);
export const AnalyticsRedirectElement = analyticsOnly(<SelectSite label="Analytics" />);
export const LegacyAnalyticsRedirectElement = analyticsOnly(<LegacyAnalyticsRedirect />);
/* A report is its own header (back button, title, stats): its routes declare
   a titleless bleed frame, so the loading fallback is inside it too. */
const reportPage = analyticsOnly;
export const ProjectOverviewElement = reportPage(
  <Suspense fallback={null}>
    <ProjectOverviewReportPage />
  </Suspense>,
);
export const ContractorAnalysisElement = reportPage(<ContractorAnalysisReportPage />);
export const WorkersTimesheetElement = reportPage(
  <Suspense fallback={null}>
    <WorkerTimesheetReportPage />
  </Suspense>,
);
export const IncidentReportElement = reportPage(<IncidentReportReportPage />);
export const DailyRouteElement = reportPage(<DailyRouteReportPage />);
export const ObserveLocationElement = reportPage(<ObserveLocationReportPage />);
export const AiAgentElement = (
  <Suspense fallback={null}>
    <AiAgentPage />
  </Suspense>
);
// Bare `/ai-agent` shows a SelectSite placeholder — the page itself
// is per-site, mirrors the CAD / Issues / Dashboard pattern after
// the SelectSite refactor.
export const AiAgentRedirectElement = <SelectSite label="AI Agent" />;
export const SchedulerElement = (
  <Suspense fallback={<SchedulerPageSkeleton />}>
    <SchedulerPage />
  </Suspense>
);
export const SchedulerRedirectElement = <SelectSite label="Scheduler" />;
export const CadElement = (
  <Suspense fallback={null}>
    <CadPage />
  </Suspense>
);
export const CadRedirectElement = <SelectSite label="CAD / BIM" />;
export const DocumentsElement         = <DocumentsPage />;
export const AnnotationsElement       = <AnnotationsPage />;
export const DocumentsRedirectElement = <SelectSite label="Documents" />;
export const LogisticsElement = <LogisticsPage />;
export const LogisticsRedirectElement = <SelectSite label="Logistics" />;
export const AdminElement = <ProtectedRoute feature="admin"><AdminPage /></ProtectedRoute>;
export const AdminRedirectElement = <ProtectedRoute feature="admin"><SelectSite label="Admin" /></ProtectedRoute>;
/* Project Merge — the one admin screen that names both upstreams by design:
   links a Genba Plus site to a Trusstor project, minting one unified site. */
export const ProjectMergeElement = <ProtectedRoute feature="admin"><ProjectMergePage /></ProtectedRoute>;

/* Bare `/monitor` lands on a site picker, mirroring the CAD / Issues
   pattern; the per-site path renders the live MonitorElement. */
export const MonitorRedirectElement        = <SelectSite label="Monitor" />;

export const CamerasElement                = <CamerasPage />;
export const CamerasRedirectElement        = <SelectSite label="Cameras" />;
export const CommsElement                  = <CommsPage />;
export const CommsRedirectElement          = <SelectSite label="Communication Hub" />;
export const EquipmentElement              = <EquipmentPage />;
export const EquipmentRedirectElement      = <SelectSite label="Equipment" />;
export const SubcontractorsElement         = <SubcontractorsPage />;
export const SubcontractorsRedirectElement = <SelectSite label="Subcontractors" />;
export const ProductivityElement           = <ProductivityPage />;
export const ProductivityRedirectElement   = <SelectSite label="Productivity" />;
/* Flow View — fully mocked Process Intelligence surface that mirrors
   the Lovable prototype. Pulls fixtures from `@/mocks/flowView.ts`;
   no backend yet (the Flow domain has no D-ORCA collection, DAITEC
   adapter, or Trusstor primitive today). */
export const FlowViewElement = <FlowViewPage />;
export const FinanceElement  = <FinancePage />;

/* Account settings — the two-factor section (status / enroll / disable).
   Reached from the user menu, not the sidebar. */
export const SecurityElement = <SecurityPage />;
/* Alert settings — the person's own, per project; every role. The bare path
   asks for a site first, as the other site-scoped pages do. */
export const AlertSettingsElement = <AlertSettingsPage />;
export const AlertSettingsRedirectElement = <SelectSite label="Alert settings" />;

export const HelpCenterElement = <HelpCenterPage />;
export const GuideReaderElement = <GuideReaderPage />;
